import { useState } from 'react';
import { 
  QrCode, Camera, X, Check, ArrowLeft, CreditCard, 
  Wallet, Star, Receipt, Upload,
  Sparkles, Zap, Shield, Info, Plus, Minus, Home, Search, User
} from 'lucide-react';
import { components } from '../theme/wunderCityTheme';

function MakeQRPayment() {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [pointsToUse, setPointsToUse] = useState(0);
  
  const userBalance = {
    points: 2847,
    dollarValue: 28.47
  };

  const merchant = {
    name: "Coastal Coffee Co.",
    category: "Coffee & Cafe",
    logo: "☕"
  };

  const transaction = {
    subtotal: 12.50,
    tax: 0.88,
    total: 13.38,
    pointsEarned: 27
  };

  return (
    <div className="w-full">
      {/* Section Header */}
      <div className="text-center px-4 md:px-8 pb-8 md:pb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Scan Receipt
        </h2>
        <p className="text-lg md:text-xl text-gray-600">
          Help the user upload receipts and track with full automation
        </p>
      </div>

      {/* Payment Screens */}
      <div className="flex flex-col lg:flex-row gap-8 p-4 md:p-8 items-center lg:justify-center overflow-x-auto">
        
        {/* Solution 1: Scanner-First Experience */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* QR Scanner View */}
                <div className="pt-11 h-full bg-black flex flex-col">
                  {/* Camera View */}
                  <div className="flex-1 relative">
                    {/* Simulated Camera Feed */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800"></div>
                    
                    {/* Scanner Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        {/* Scanner Frame */}
                        <div className="w-64 h-64 relative">
                          {/* Corner Markers */}
                          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-white rounded-tl-2xl"></div>
                          <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-white rounded-tr-2xl"></div>
                          <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-white rounded-bl-2xl"></div>
                          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-white rounded-br-2xl"></div>
                          
                          {/* Scanning Line Animation */}
                          <div className="absolute top-2 left-2 right-2 h-1 bg-gradient-to-r from-transparent via-sky-400 to-transparent opacity-80 animate-pulse"></div>
                        </div>
                        
                        {/* QR Code Icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <QrCode className="w-16 h-16 text-white/20" />
                        </div>
                      </div>
                    </div>

                    {/* Top Controls */}
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-6">
                      <div className="flex items-center justify-between">
                        <button className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                          <X className="w-5 h-5 text-white" />
                        </button>
                        <h1 className="text-lg font-semibold text-white">Scan Reciept</h1>
                        <button className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                          <Zap className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>

                    {/* Bottom Info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                      <div className="text-center mb-6 mt-4">
                        <p className="text-white text-lg mb-2">
                          Position Receipt<br />within frame
                        </p>
                        <p className="text-white/60 text-sm">Scanning automatically</p>
                      </div>
                      
                      {/* Quick Info */}
                      <div className="bg-white/10 backdrop-blur rounded-2xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white/80 text-sm">Your Balance</p>
                          <p className="text-white font-semibold">{userBalance.points} pts</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-400" />
                          <p className="text-green-400 text-xs">Secure payment processing</p>
                        </div>
                      </div>

                      {/* Alternative Actions */}
                      <div className="grid grid-cols-2 gap-3">
                        <button className="bg-white/20 backdrop-blur text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2">
                          <Upload className="w-4 h-4" />
                          Upload Receipt
                        </button>
                        <button className="bg-white/20 backdrop-blur text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2">
                          <Receipt className="w-4 h-4" />
                          Enter Code
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Immersive Scanner</p>
        </div>

        {/* Solution 2: Payment Details Focus */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Payment Details View */}
                <div className="pt-11 h-full bg-gray-50 flex flex-col">
                  {/* Header */}
                  <div className="bg-white px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <button className="w-10 h-10 rounded-full flex items-center justify-center">
                        <ArrowLeft className="w-5 h-5 text-gray-700" />
                      </button>
                      <h1 className="text-lg font-semibold text-gray-900">Payment Details</h1>
                      <button className="w-10 h-10 rounded-full flex items-center justify-center">
                        <Info className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto pb-24">
                    {/* Merchant Info */}
                    <div className="bg-white px-6 py-5 mb-1">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center text-2xl">
                          {merchant.logo}
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">{merchant.name}</h2>
                          <p className="text-sm text-gray-600">{merchant.category}</p>
                        </div>
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-white px-6 py-5 mb-1">
                      <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <p className="text-gray-600">Subtotal</p>
                          <p className="font-medium">${transaction.subtotal.toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-gray-600">Tax</p>
                          <p className="font-medium">${transaction.tax.toFixed(2)}</p>
                        </div>
                        <div className="border-t pt-2 mt-2">
                          <div className="flex justify-between">
                            <p className="font-semibold text-gray-900">Total</p>
                            <p className="font-bold text-lg text-gray-900">${transaction.total.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Points Earned */}
                      <div className="mt-4 bg-green-50 rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-green-600" />
                          <p className="text-sm font-medium text-green-900">Points you'll earn</p>
                        </div>
                        <p className="font-bold text-green-600">+{transaction.pointsEarned}</p>
                      </div>
                    </div>

                    {/* Points Usage */}
                    <div className="bg-white px-6 py-5 mb-1">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">Use Points</h3>
                        <p className="text-sm text-gray-600">{userBalance.points} available</p>
                      </div>
                      
                      <div className="flex items-center gap-4 mb-4">
                        <button 
                          onClick={() => setPointsToUse(Math.max(0, pointsToUse - 100))}
                          className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
                        >
                          <Minus className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="flex-1 text-center">
                          <p className="text-2xl font-bold text-gray-900">{pointsToUse}</p>
                          <p className="text-sm text-gray-600">= ${(pointsToUse / 100).toFixed(2)}</p>
                        </div>
                        <button 
                          onClick={() => setPointsToUse(Math.min(userBalance.points, pointsToUse + 100))}
                          className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
                        >
                          <Plus className="w-5 h-5 text-gray-600" />
                        </button>
                      </div>
                      
                      {/* Quick Select */}
                      <div className="grid grid-cols-3 gap-2">
                        <button 
                          onClick={() => setPointsToUse(500)}
                          className="bg-gray-100 py-2 rounded-xl text-sm font-medium text-gray-700"
                        >
                          500 pts
                        </button>
                        <button 
                          onClick={() => setPointsToUse(1000)}
                          className="bg-gray-100 py-2 rounded-xl text-sm font-medium text-gray-700"
                        >
                          1000 pts
                        </button>
                        <button 
                          onClick={() => setPointsToUse(Math.min(userBalance.points, transaction.total * 100))}
                          className="bg-sky-100 py-2 rounded-xl text-sm font-medium text-sky-700"
                        >
                          Max
                        </button>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="bg-white px-6 py-5">
                      <h3 className="font-semibold text-gray-900 mb-3">Payment Method</h3>
                      <div className="space-y-2">
                        <button 
                          onClick={() => setSelectedPaymentMethod('card')}
                          className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                            selectedPaymentMethod === 'card' 
                              ? 'border-sky-500 bg-sky-50' 
                              : 'border-gray-200'
                          }`}
                        >
                          <CreditCard className="w-5 h-5 text-gray-600" />
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-900">•••• 4242</p>
                            <p className="text-sm text-gray-600">Visa</p>
                          </div>
                          {selectedPaymentMethod === 'card' && (
                            <Check className="w-5 h-5 text-sky-600" />
                          )}
                        </button>
                        
                        <button 
                          onClick={() => setSelectedPaymentMethod('wallet')}
                          className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                            selectedPaymentMethod === 'wallet' 
                              ? 'border-sky-500 bg-sky-50' 
                              : 'border-gray-200'
                          }`}
                        >
                          <Wallet className="w-5 h-5 text-gray-600" />
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-900">Apple Pay</p>
                            <p className="text-sm text-gray-600">Default</p>
                          </div>
                          {selectedPaymentMethod === 'wallet' && (
                            <Check className="w-5 h-5 text-sky-600" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Pay Button */}
                  <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-gray-600">Amount Due</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${(transaction.total - (pointsToUse / 100)).toFixed(2)}
                      </p>
                    </div>
                    <button className={`w-full ${components.button.primary} ${components.button.padding.lg} ${components.button.rounded} text-lg font-semibold flex items-center justify-center gap-2`}>
                      <QrCode className="w-5 h-5" />
                      Scan & Pay
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Detailed Breakdown</p>
        </div>

        {/* Solution 3: Quick Payment Flow */}
        <div className="flex flex-col items-center gap-1 md:gap-2 lg:gap-4">
          <div className="relative flex-shrink-0 h-[609px] md:h-[731px] lg:h-[812px] flex items-center justify-center">
            <div className={`transform ${components.phoneMockup.scale.mobile} ${components.phoneMockup.scale.tablet} ${components.phoneMockup.scale.desktop} origin-center`}>
            <div className={components.phoneMockup.outer}>
              <div className={components.phoneMockup.inner}>
                {/* Status Bar */}
                <div className={components.statusBar.container}>
                  <span className={components.statusBar.time}>9:41</span>
                  <div className={components.statusBar.battery}>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                    <div className="w-4 h-3 bg-black rounded-sm"></div>
                  </div>
                </div>

                {/* Quick Pay Success View */}
                <div className="pt-11 h-full bg-white flex flex-col">
                  {/* Scanner Preview Window */}
                  <div className="relative h-48 bg-black">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800 opacity-50"></div>
                    <Camera className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 text-white/30" />
                    
                    {/* Success Overlay */}
                    <div className="absolute inset-0 bg-green-500/90 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                          <Check className="w-8 h-8 text-green-500" />
                        </div>
                        <p className="text-white font-semibold">QR Code Scanned!</p>
                      </div>
                    </div>
                  </div>

                  {/* Merchant & Amount */}
                  <div className="px-6 py-6 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-xl">
                          {merchant.logo}
                        </div>
                        <div>
                          <h2 className="font-semibold text-gray-900">{merchant.name}</h2>
                          <p className="text-sm text-gray-600">Order #2847</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">${transaction.total}</p>
                        <p className="text-sm text-green-600 font-medium">+{transaction.pointsEarned} pts</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="px-6 -mt-2">
                    <div className="grid grid-cols-3 gap-3">
                      <button className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <Wallet className="w-6 h-6 text-sky-600 mx-auto mb-2" />
                        <p className="text-xs font-medium text-gray-700">Pay Full</p>
                        <p className="text-xs text-gray-500 mt-1">${transaction.total}</p>
                      </button>
                      <button className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 shadow-sm border border-orange-100">
                        <Star className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                        <p className="text-xs font-medium text-gray-700">Use Points</p>
                        <p className="text-xs text-orange-600 mt-1">Save ${(userBalance.points / 100).toFixed(0)}</p>
                      </button>
                      <button className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                        <CreditCard className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                        <p className="text-xs font-medium text-gray-700">Split</p>
                        <p className="text-xs text-gray-500 mt-1">Custom</p>
                      </button>
                    </div>
                  </div>

                  {/* Receipt Options */}
                  <div className="px-6 mt-6">
                    <div className="bg-gray-50 rounded-2xl p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Receipt Preference</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <button className="bg-white border border-gray-200 rounded-xl py-3 flex items-center justify-center gap-2">
                          <Receipt className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Digital</span>
                        </button>
                        <button className="bg-white border border-gray-200 rounded-xl py-3 flex items-center justify-center gap-2">
                          <Upload className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Paper + Scan</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Tips Section */}
                  <div className="px-6 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Add Tip</p>
                    <div className="flex gap-2">
                      <button className="flex-1 bg-gray-100 py-2 rounded-xl text-sm font-medium text-gray-700">15%</button>
                      <button className="flex-1 bg-gray-100 py-2 rounded-xl text-sm font-medium text-gray-700">18%</button>
                      <button className="flex-1 bg-sky-100 py-2 rounded-xl text-sm font-medium text-sky-700 border-2 border-sky-200">20%</button>
                      <button className="flex-1 bg-gray-100 py-2 rounded-xl text-sm font-medium text-gray-700">Custom</button>
                    </div>
                  </div>

                  {/* Bottom Actions */}
                  <div className="flex-1 flex items-end">
                    <div className="w-full p-6 space-y-3">
                      <button className={`w-full ${components.button.primary} py-4 rounded-2xl font-semibold text-lg`}>
                        Pay ${(transaction.total * 1.20).toFixed(2)}
                      </button>
                      <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-2xl font-medium">
                        More Options
                      </button>
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className={components.tabBar.container}>
                    <div className="flex items-center justify-around py-2">
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Home className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Home</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Search className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Discover</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <Wallet className="w-6 h-6 text-sky-600" />
                        <span className="text-xs text-sky-600">Wallet</span>
                      </button>
                      <button className="flex flex-col items-center gap-1 py-2 px-4">
                        <User className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-600">Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <p className={`${components.mockupLabel.className} ${components.mockupLabel.spacing.top} ${components.mockupLabel.spacing.bottom}`}>Express Checkout</p>
        </div>

      </div>
    </div>
  );
}

export default MakeQRPayment;